import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  X, Save, Loader2, Sparkles, User, Calendar, Phone, Mail, MapPin,
  CreditCard as IdCard, CheckCircle2, AtSign,
} from "lucide-react";
import { cliente360Api } from "../../lib/api";

interface Props {
  cliente: {
    codCliente: number;
    nombre: string | null;
    cif?: string | null;
    email?: string | null;
    telefono1?: string | null;
    mobil?: string | null;
    direccion?: string | null;
    poblacion?: string | null;
    fechaNacimiento?: string | null;
    sexo?: string | null;
    instagram?: string | null;
    twitter?: string | null;
    facebook?: string | null;
    tiktok?: string | null;
  };
  open: boolean;
  onClose: () => void;
}

const CONFIANZA_LABELS = {
  alta: { label: "Alta confianza", color: "bg-emerald-100 text-emerald-700" },
  media: { label: "Confianza media", color: "bg-amber-100 text-amber-700" },
  baja: { label: "Baja confianza", color: "bg-ink-100 text-ink-600" },
};

export function EditarDatosModal({ cliente, open, onClose }: Props) {
  const qc = useQueryClient();

  const [nombre, setNombre] = useState(cliente.nombre ?? "");
  const [cif, setCif] = useState(cliente.cif ?? "");
  const [email, setEmail] = useState(cliente.email ?? "");
  const [telefono1, setTelefono1] = useState(cliente.telefono1 ?? "");
  const [mobil, setMobil] = useState(cliente.mobil ?? "");
  const [direccion, setDireccion] = useState(cliente.direccion ?? "");
  const [poblacion, setPoblacion] = useState(cliente.poblacion ?? "");
  const [fechaNacimiento, setFechaNacimiento] = useState(
    cliente.fechaNacimiento ? cliente.fechaNacimiento.substring(0, 10) : ""
  );
  const [sexo, setSexo] = useState<"M" | "F" | "">(
    (cliente.sexo as any) ?? ""
  );
  const [instagram, setInstagram] = useState(cliente.instagram ?? "");
  const [twitter, setTwitter] = useState(cliente.twitter ?? "");
  const [facebook, setFacebook] = useState(cliente.facebook ?? "");
  const [tiktok, setTiktok] = useState(cliente.tiktok ?? "");
  const [sugerencia, setSugerencia] = useState<{
    sugerencia: "M" | "F" | null; confianza: "alta" | "media" | "baja"; razon: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setNombre(cliente.nombre ?? "");
      setCif(cliente.cif ?? "");
      setEmail(cliente.email ?? "");
      setTelefono1(cliente.telefono1 ?? "");
      setMobil(cliente.mobil ?? "");
      setDireccion(cliente.direccion ?? "");
      setPoblacion(cliente.poblacion ?? "");
      setFechaNacimiento(cliente.fechaNacimiento ? cliente.fechaNacimiento.substring(0, 10) : "");
      setSexo((cliente.sexo as any) ?? "");
      setInstagram(cliente.instagram ?? "");
      setTwitter(cliente.twitter ?? "");
      setFacebook(cliente.facebook ?? "");
      setTiktok(cliente.tiktok ?? "");
      setSugerencia(null);
      setError(null);
    }
  }, [cliente.codCliente, open]);

  const sugerirMut = useMutation({
    mutationFn: () => cliente360Api.sugerirSexo(cliente.codCliente),
    onSuccess: (data) => {
      setSugerencia({
        sugerencia: data.sugerencia,
        confianza: data.confianza,
        razon: data.razon,
      });
    },
  });

  const guardarMut = useMutation({
    mutationFn: () => cliente360Api.actualizarDatos(cliente.codCliente, {
      nombreCliente: nombre,
      cif: cif || null,
      email: email || null,
      telefono1: telefono1 || null,
      mobil: mobil || null,
      direccion1: direccion || null,
      poblacion: poblacion || null,
      fechaNacimiento: fechaNacimiento || null,
      sexo: sexo || null,
      instagram: instagram || null,
      twitter: twitter || null,
      facebook: facebook || null,
      tiktok: tiktok || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cliente360"] });
      onClose();
    },
    onError: (e: any) => setError(e.message || "Error guardando"),
  });

  function aplicarSugerencia() {
    if (sugerencia?.sugerencia) {
      setSexo(sugerencia.sugerencia);
    }
  }

  function calcularEdad(fecha: string): number | null {
    if (!fecha) return null;
    const d = new Date(fecha);
    const hoy = new Date();
    let edad = hoy.getFullYear() - d.getFullYear();
    const m = hoy.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < d.getDate())) edad--;
    return edad >= 0 && edad < 150 ? edad : null;
  }

  if (!open) return null;

  const edad = calcularEdad(fechaNacimiento);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>

        <div className="flex items-center justify-between px-5 py-3 border-b border-ink-200">
          <h2 className="font-display font-bold text-ink-800 inline-flex items-center gap-2">
            <User className="size-5 text-brand-600" />
            Editar datos personales
          </h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700">
            <X className="size-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {error && (
            <div className="px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Nombre */}
          <div>
            <label className="block text-xs font-medium text-ink-600 mb-1">Nombre completo *</label>
            <input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)}
              className="input w-full" />
          </div>

          {/* Cedula y sexo en fila */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1 inline-flex items-center gap-1">
                <IdCard className="size-3" /> Documento de identidad
              </label>
              <input type="text" value={cif} onChange={(e) => setCif(e.target.value)}
                placeholder="V12345678 / J123456789"
                className="input w-full font-mono" />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1">Sexo</label>
              <div className="flex gap-2">
                <button onClick={() => setSexo("M")}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                    ${sexo === "M" ? "bg-blue-600 text-white" : "bg-ink-100 text-ink-700 hover:bg-ink-200"}`}>
                  ♂ Masculino
                </button>
                <button onClick={() => setSexo("F")}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                    ${sexo === "F" ? "bg-pink-600 text-white" : "bg-ink-100 text-ink-700 hover:bg-ink-200"}`}>
                  ♀ Femenino
                </button>
                {sexo !== "" && (
                  <button onClick={() => setSexo("")}
                    className="px-3 py-2 rounded-lg text-sm font-medium bg-ink-100 hover:bg-ink-200 text-ink-500"
                    title="Limpiar">
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Sugerencia de sexo */}
          <div className="rounded-lg border border-brand-200 bg-brand-50/50 p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs text-ink-700">
                <Sparkles className="size-3.5 inline mr-1 text-brand-600" />
                <span className="font-medium">Sugerir sexo por el nombre</span>
              </div>
              <button onClick={() => sugerirMut.mutate()} disabled={sugerirMut.isPending || !nombre}
                className="text-xs px-2.5 py-1 rounded bg-brand-600 hover:bg-brand-700 text-white font-medium disabled:opacity-50 inline-flex items-center gap-1">
                {sugerirMut.isPending ? <Loader2 className="size-3 animate-spin" /> : <Sparkles className="size-3" />}
                Analizar nombre
              </button>
            </div>

            {sugerencia && (
              <div className="mt-2 flex items-center justify-between gap-2 text-xs">
                <div>
                  Sugerencia:{" "}
                  {sugerencia.sugerencia === "M" && <strong className="text-blue-700">♂ Masculino</strong>}
                  {sugerencia.sugerencia === "F" && <strong className="text-pink-700">♀ Femenino</strong>}
                  {sugerencia.sugerencia === null && <strong className="text-ink-500">No se pudo determinar</strong>}
                  {sugerencia.sugerencia && (
                    <span className={`ml-2 px-1.5 py-0.5 rounded font-medium ${CONFIANZA_LABELS[sugerencia.confianza].color}`}>
                      {CONFIANZA_LABELS[sugerencia.confianza].label}
                    </span>
                  )}
                  <div className="text-ink-500 italic mt-0.5">{sugerencia.razon}</div>
                </div>
                {sugerencia.sugerencia && sexo !== sugerencia.sugerencia && (
                  <button onClick={aplicarSugerencia}
                    className="text-xs px-2 py-1 rounded bg-brand-600 hover:bg-brand-700 text-white font-medium inline-flex items-center gap-1 shrink-0">
                    <CheckCircle2 className="size-3" /> Aplicar
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Fecha nacimiento */}
          <div>
            <label className="block text-xs font-medium text-ink-600 mb-1 inline-flex items-center gap-1">
              <Calendar className="size-3" /> Fecha de nacimiento
            </label>
            <div className="flex gap-2 items-center">
              <input type="date" value={fechaNacimiento}
                onChange={(e) => setFechaNacimiento(e.target.value)}
                className="input flex-1" />
              {edad !== null && (
                <span className="px-3 py-2 bg-brand-50 text-brand-700 rounded-lg text-sm font-medium shrink-0">
                  {edad} años
                </span>
              )}
            </div>
          </div>

          {/* Contacto */}
          <hr className="border-ink-200" />
          <h3 className="text-sm font-bold text-ink-800">Contacto</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1 inline-flex items-center gap-1">
                <Phone className="size-3" /> Teléfono
              </label>
              <input type="tel" value={telefono1} onChange={(e) => setTelefono1(e.target.value)}
                placeholder="0212-1234567" className="input w-full" />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1 inline-flex items-center gap-1">
                <Phone className="size-3" /> Móvil
              </label>
              <input type="tel" value={mobil} onChange={(e) => setMobil(e.target.value)}
                placeholder="04141234567" className="input w-full" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-600 mb-1 inline-flex items-center gap-1">
              <Mail className="size-3" /> Email
            </label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="input w-full" />
          </div>

          {/* Dirección */}
          <hr className="border-ink-200" />
          <h3 className="text-sm font-bold text-ink-800">Dirección</h3>
          <div>
            <label className="block text-xs font-medium text-ink-600 mb-1 inline-flex items-center gap-1">
              <MapPin className="size-3" /> Dirección
            </label>
            <input type="text" value={direccion} onChange={(e) => setDireccion(e.target.value)}
              placeholder="Calle, avenida..." className="input w-full" />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-600 mb-1">Población / Ciudad</label>
            <input type="text" value={poblacion} onChange={(e) => setPoblacion(e.target.value)}
              className="input w-full" />
          </div>

          {/* Redes sociales */}
          <hr className="border-ink-200" />
          <h3 className="text-sm font-bold text-ink-800">Redes sociales</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1 inline-flex items-center gap-1">
                <AtSign className="size-3" /> Instagram
              </label>
              <input type="text" value={instagram} onChange={(e) => setInstagram(e.target.value)}
                placeholder="@usuario" className="input w-full" />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1 inline-flex items-center gap-1">
                <AtSign className="size-3" /> X (Twitter)
              </label>
              <input type="text" value={twitter} onChange={(e) => setTwitter(e.target.value)}
                placeholder="@usuario" className="input w-full" />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1 inline-flex items-center gap-1">
                <AtSign className="size-3" /> Facebook
              </label>
              <input type="text" value={facebook} onChange={(e) => setFacebook(e.target.value)}
                placeholder="Nombre de perfil o link" className="input w-full" />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-600 mb-1 inline-flex items-center gap-1">
                <AtSign className="size-3" /> TikTok
              </label>
              <input type="text" value={tiktok} onChange={(e) => setTiktok(e.target.value)}
                placeholder="@usuario" className="input w-full" />
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-ink-200 bg-ink-50 flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary">Cancelar</button>
          <button onClick={() => guardarMut.mutate()} disabled={guardarMut.isPending || !nombre.trim()}
            className="px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
            {guardarMut.isPending ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
            Guardar cambios
          </button>
        </div>
      </div>
    </div>
  );
}
